import React, { useState } from 'react';
import { apiService } from '../services/api';

const LoanQualificationSimulator = ({ onBackToHome }) => {
  const [formData, setFormData] = useState({
    creditScore: '',
    propertyType: '',
    situationDescription: '',
    loanAmount: '',
    annualIncome: '',
    downPayment: '',
    purchasePrice: '',
    veteranStatus: '',
    loanType: ''
  });
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Key parameters to help AI focus analysis
  const creditScoreRanges = [
    { value: '500-579', label: '500-579 (Challenged Credit)' },
    { value: '580-619', label: '580-619 (Fair Credit)' },
    { value: '620-659', label: '620-659 (Good Credit)' },
    { value: '660-699', label: '660-699 (Very Good Credit)' },
    { value: '700-739', label: '700-739 (Excellent Credit)' },
    { value: '740+', label: '740+ (Exceptional Credit)' },
    { value: 'unknown', label: "I don't know my credit score" }
  ];

  const propertyTypes = [
    { value: 'primary-sfr', label: 'Primary Residence - Single Family' },
    { value: 'primary-condo', label: 'Primary Residence - Condo/Townhome' },
    { value: 'primary-multi', label: 'Primary Residence - 2-4 Units' },
    { value: 'investment', label: 'Investment Property' },
    { value: 'second-home', label: 'Second/Vacation Home' },
    { value: 'manufactured', label: 'Manufactured Home' },
    { value: 'rural', label: 'Rural Property (Potential USDA)' }
  ];

  const veteranStatusOptions = [
    { value: '', label: 'Select veteran status' },
    { value: 'eligible', label: 'Eligible Veteran (Active/Veteran/Guard/Reserve)' },
    { value: 'spouse', label: 'Surviving Spouse of Veteran' },
    { value: 'not-eligible', label: 'Not Eligible for VA Benefits' },
    { value: 'unsure', label: 'Unsure about VA Eligibility' }
  ];

  const loanTypeOptions = [
    { value: '', label: 'Select loan purpose' },
    { value: 'purchase', label: 'Purchase - Buying a home' },
    { value: 'refinance-rate-term', label: 'Refinance - Rate & Term (no cash out)' },
    { value: 'refinance-cash-out', label: 'Refinance - Cash Out' },
    { value: 'construction', label: 'Construction Loan' },
    { value: 'renovation', label: 'Renovation/Rehab Loan' }
  ];

  const validateFields = () => {
    const errors = {};
    if (!formData.creditScore) errors.creditScore = 'Credit score is required.';
    if (!formData.propertyType) errors.propertyType = 'Property type is required.';
    if (!formData.loanType) errors.loanType = 'Loan purpose is required.';
    if (!formData.purchasePrice) errors.purchasePrice = 'Purchase price is required.';
    if (!formData.loanAmount) errors.loanAmount = 'Loan amount is required.';
    if (!formData.annualIncome) errors.annualIncome = 'Annual income is required.';
    if (!formData.situationDescription.trim()) errors.situationDescription = 'Situation description is required.';
    if (!formData.veteranStatus) errors.veteranStatus = 'Veteran status is required.';
    return errors;
  };

  const runQualificationAnalysis = async () => {
    const errors = validateFields();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError(null);
    setLastRequest(null);
    setLastResponse(null);
    try {
      // Prepare data for API call
      const qualificationData = {
        credit_score: formData.creditScore,
        property_type: formData.propertyType,
        situation_description: formData.situationDescription,
        loan_amount: formData.loanAmount ? parseInt(formData.loanAmount) : null,
        annual_income: formData.annualIncome ? parseInt(formData.annualIncome) : null,
        down_payment: formData.downPayment && formData.downPayment !== 'N/A' ? parseInt(formData.downPayment) : null,
        purchase_price: formData.purchasePrice ? parseInt(formData.purchasePrice) : null,
        veteran_status: formData.veteranStatus,
        loan_type: formData.loanType
      };
      setLastRequest(qualificationData);
      // Call real AI analysis API
      const analysisResult = await apiService.analyzeLoanQualification(qualificationData);
      setLastResponse(analysisResult);
      setResults(analysisResult);
    } catch (err) {
      let errorMsg = 'AI analysis temporarily unavailable.';
      if (err && err.message) {
        errorMsg += ` Error: ${err.message}`;
      }
      if (err && err.response) {
        errorMsg += ` (Status: ${err.response.status})`;
      }
      setError(errorMsg);
      setLastResponse(err);
      // No mock fallback in production
    } finally {
      setLoading(false);
    }
  };

  // All analysis is now handled by the live AI API only

  const handleSubmit = async (e) => {
    const downPayment = formData.downPayment === 'N/A' ? 0 : (parseInt(formData.downPayment) || (purchasePrice * 0.2));
    const ltv = purchasePrice > 0 ? ((purchasePrice - downPayment) / purchasePrice) * 100 : 0;
    const dti = (loanAmount * 0.004 * 12) / income; // Rough DTI calculation
    
    // Conventional Analysis
    if (creditNum >= 620 && dti < 0.43 && ltv <= 97) {
      programs.push({
        type: 'conventional',
        qualified: true,
        confidence: ltv <= 80 ? 95 : 85,
        details: `Strong conventional candidate. LTV: ${ltv.toFixed(1)}%, DTI: ${(dti * 100).toFixed(1)}%`,
        requirements: [
          ltv > 80 ? 'PMI required (LTV > 80%)' : '20% down - no PMI required',
          'Stable employment history', 
          'Debt-to-income under 43%',
          `Credit score ${creditNum} meets requirements`
        ],
        nextSteps: ['Get pre-approved', 'Shop for rates', 'Consider down payment options']
      });
    } else if (creditNum >= 620) {
      programs.push({
        type: 'conventional',
        qualified: 'conditional',
        confidence: 60,
        details: `May qualify with conditions. LTV: ${ltv.toFixed(1)}%, DTI: ${(dti * 100).toFixed(1)}%`,
        requirements: [
          dti >= 0.43 ? 'DTI too high - need to reduce debt or increase income' : 'DTI acceptable',
          ltv > 97 ? 'Down payment too low for conventional' : 'LTV acceptable',
          'Consider compensating factors'
        ],
        nextSteps: ['Improve DTI ratio', 'Consider larger down payment', 'Explore government options']
      });
    }
    
    // FHA Analysis
    if (creditNum >= 500 && ltv <= 96.5) {
      const requiresTenPercent = creditNum < 580;
      const actualDownPayment = requiresTenPercent ? 10 : 3.5;
      programs.push({
        type: 'fha',
        qualified: true,
        confidence: creditNum >= 580 ? 90 : 75,
        details: `${requiresTenPercent ? 'FHA eligible with 10% down' : 'Excellent FHA candidate with 3.5% down'}. LTV: ${ltv.toFixed(1)}%`,
        requirements: [
          `${actualDownPayment}% down payment required`,
          'FHA mortgage insurance (upfront + annual)',
          'Primary residence only',
          `Credit score ${creditNum} ${requiresTenPercent ? 'requires 10% down' : 'qualifies for 3.5% down'}`
        ],
        nextSteps: ['Find FHA-approved lender', 'Verify property meets FHA standards', 'Budget for mortgage insurance']
      });
    } else if (creditNum >= 500) {
      programs.push({
        type: 'fha',
        qualified: 'conditional',
        confidence: 50,
        details: `FHA may be possible with larger down payment. Current LTV: ${ltv.toFixed(1)}%`,
        requirements: [
          'LTV exceeds 96.5% FHA limit',
          'Need larger down payment',
          'Manual underwriting likely required'
        ],
        nextSteps: ['Increase down payment', 'Consider waiting to save more', 'Speak with FHA specialist']
      });
    }
    
    // VA Analysis - enhanced with veteran status
    const isVeteranEligible = formData.veteranStatus === 'eligible' || formData.veteranStatus === 'spouse';
    const isVeteranUnsure = formData.veteranStatus === 'unsure';
    
    if (isVeteranEligible || isVeteranUnsure) {
      programs.push({
        type: 'va',
        qualified: isVeteranEligible ? (creditNum >= 620 ? 'yes' : 'conditional') : 'conditional',
        confidence: isVeteranEligible ? (creditNum >= 620 ? 95 : 80) : 60,
        details: isVeteranEligible 
          ? `VA loan available with no down payment. Credit score ${creditNum} ${creditNum >= 620 ? 'meets typical lender requirements' : 'may require manual underwriting'}.`
          : 'VA eligibility needs verification. If eligible, offers significant benefits including no down payment.',
        requirements: isVeteranEligible 
          ? ['Certificate of Eligibility (COE)', 'Property meets VA standards', 'Funding fee (may be waived)', 'No down payment required']
          : ['Verify military service eligibility', 'Obtain Certificate of Eligibility', 'Property meets VA standards'],
        nextSteps: isVeteranEligible
          ? ['Obtain COE if not already done', 'Find VA-approved lender', 'Get property VA appraisal']
          : ['Verify VA eligibility status', 'Contact VA regional office', 'Gather military service records']
      });
    } else if (formData.veteranStatus === 'not-eligible' || formData.veteranStatus === '') {
      // Only show if explicitly not eligible or unknown
      if (formData.veteranStatus === '') {
        programs.push({
          type: 'va',
          qualified: 'conditional',
          confidence: 30,
          details: 'VA eligibility unknown. If you have military service history, VA loans offer significant benefits.',
          requirements: ['Military service verification', 'Certificate of Eligibility', 'Property meets VA standards'],
          nextSteps: ['Check if you qualify for VA benefits', 'Verify military service eligibility']
        });
      }
    }
    
    // USDA Analysis
    if (formData.propertyType === 'rural') {
      programs.push({
        type: 'usda',
        qualified: income < 120000 ? true : 'conditional',
        confidence: 80,
        details: 'Property location and income appear USDA eligible.',
        requirements: ['Property in USDA eligible area', 'Income limits apply', 'Primary residence only'],
        nextSteps: ['Verify property eligibility', 'Check income limits for area']
      });
    }

    return {
      programs,
      overallAssessment: programs.some(p => p.qualified === true) 
        ? 'You appear to have viable loan options!' 
        : 'Some challenges identified, but options may be available.',
      aiInsights: [
        'Based on your situation, government loan programs may offer advantages',
        'Consider improving credit score for better conventional rates',
        'Multiple loan types available - compare terms and benefits'
      ]
    };
  };

  const resetForm = () => {
    setFormData({
      creditScore: '',
      propertyType: '',
      situationDescription: '',
      loanAmount: '',
      annualIncome: '',
      downPayment: '',
      purchasePrice: '',
      veteranStatus: '',
      loanType: ''
    });
    setResults(null);
    setError(null);
  };

  const getQualificationIcon = (qualified) => {
    if (qualified === 'yes' || qualified === true) return '✅';
    if (qualified === 'conditional') return '⚠️';
    return '❌';
  };

  const getQualificationColor = (qualified) => {
    if (qualified === 'yes' || qualified === true) return 'text-green-600';
    if (qualified === 'conditional') return 'text-yellow-600';
    return 'text-red-600';
  };

  const getLoanTypeLabel = (type) => {
    const labels = {
      conventional: 'Conventional',
      fha: 'FHA',
      va: 'VA',
      usda: 'USDA'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="text-4xl mr-3">🤖</span>
                AI Loan Qualification Simulator
              </h1>
              <p className="text-gray-600 mt-2">
                Describe your situation and get instant qualification analysis across all major loan programs
              </p>
            </div>
            <button
              onClick={onBackToHome}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-2xl mr-2">📋</span>
              Tell Us About Your Situation
            </h2>

            <div className="space-y-6">
              {/* Key Parameters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Score Range
                  </label>
                  <select
                    value={formData.creditScore}
                    onChange={(e) => setFormData({...formData, creditScore: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select credit score range</option>
                    {creditScoreRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.creditScore && <div className="text-red-600 text-xs mt-1">{fieldErrors.creditScore}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type
                  </label>
                  <select
                    value={formData.propertyType}
                    onChange={(e) => setFormData({...formData, propertyType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select property type</option>
                    {propertyTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.propertyType && <div className="text-red-600 text-xs mt-1">{fieldErrors.propertyType}</div>}
                </div>
              </div>

              {/* Loan Type and Veteran Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Purpose
                  </label>
                  <select
                    value={formData.loanType}
                    onChange={(e) => setFormData({...formData, loanType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {loanTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.loanType && <div className="text-red-600 text-xs mt-1">{fieldErrors.loanType}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Veteran Status
                  </label>
                  <select
                    value={formData.veteranStatus}
                    onChange={(e) => setFormData({...formData, veteranStatus: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {veteranStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.veteranStatus && <div className="text-red-600 text-xs mt-1">{fieldErrors.veteranStatus}</div>}
                </div>
              </div>

              {/* Purchase Price and Down Payment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Price / Property Value
                  </label>
                  <input
                    type="number"
                    value={formData.purchasePrice}
                    onChange={(e) => setFormData({...formData, purchasePrice: e.target.value})}
                    placeholder="e.g. 500000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {fieldErrors.purchasePrice && <div className="text-red-600 text-xs mt-1">{fieldErrors.purchasePrice}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Down Payment Amount
                    <span className="text-xs text-gray-500 ml-1">(Select N/A for refinances)</span>
                  </label>
                  <div className="space-y-2">
                    <select
                      value={formData.downPayment === 'N/A' ? 'N/A' : (formData.downPayment ? 'custom' : '')}
                      onChange={(e) => {
                        if (e.target.value === 'N/A') {
                          setFormData({...formData, downPayment: 'N/A'});
                        } else if (e.target.value === 'custom') {
                          setFormData({...formData, downPayment: ''});
                        } else {
                          setFormData({...formData, downPayment: ''});
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Enter down payment amount</option>
                      <option value="N/A">N/A (Refinance/Already own property)</option>
                      <option value="custom">Custom amount</option>
                    </select>
                    {formData.downPayment !== 'N/A' && (
                      <input
                        type="number"
                        value={formData.downPayment === 'N/A' ? '' : formData.downPayment}
                        onChange={(e) => setFormData({...formData, downPayment: e.target.value})}
                        placeholder="e.g. 100000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Show calculated LTV if both values are entered */}
              {formData.purchasePrice && formData.downPayment && formData.downPayment !== 'N/A' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-sm text-blue-800">
                    <strong>Calculated LTV:</strong> {(((parseInt(formData.purchasePrice) - parseInt(formData.downPayment)) / parseInt(formData.purchasePrice)) * 100).toFixed(1)}%
                    <span className="ml-2 text-xs">
                      (Loan Amount: ${(parseInt(formData.purchasePrice) - parseInt(formData.downPayment)).toLocaleString()})
                    </span>
                  </div>
                </div>
              )}
              
              {/* Show refinance note if down payment is N/A */}
              {formData.downPayment === 'N/A' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="text-sm text-purple-800">
                    <strong>Refinance Mode:</strong> LTV will be calculated based on current property value and loan amount
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approximate Loan Amount
                  </label>
                  <input
                    type="number"
                    value={formData.loanAmount}
                    onChange={(e) => setFormData({...formData, loanAmount: e.target.value})}
                    placeholder="e.g. 400000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {fieldErrors.loanAmount && <div className="text-red-600 text-xs mt-1">{fieldErrors.loanAmount}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Income
                  </label>
                  <input
                    type="number"
                    value={formData.annualIncome}
                    onChange={(e) => setFormData({...formData, annualIncome: e.target.value})}
                    placeholder="e.g. 80000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {fieldErrors.annualIncome && <div className="text-red-600 text-xs mt-1">{fieldErrors.annualIncome}</div>}
                </div>
              </div>

              {/* Situation Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe Your Situation
                </label>
                <textarea
                  value={formData.situationDescription}
                  onChange={(e) => setFormData({...formData, situationDescription: e.target.value})}
                  rows={5}
                  placeholder="Tell us about your employment, income sources, any challenges, timeline, etc. For example: 'Self-employed for 2 years, variable income, recently divorced, looking to buy first home, have savings for down payment but concerned about debt-to-income ratio...'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                {fieldErrors.situationDescription && <div className="text-red-600 text-xs mt-1">{fieldErrors.situationDescription}</div>}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={runQualificationAnalysis}
                  disabled={loading || !formData.situationDescription.trim()}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      AI Analyzing...
                    </div>
                  ) : (
                    '🤖 Analyze Qualification'
                  )}
                </button>
                <button
                  onClick={resetForm}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
              </div>

              {/* Helper text when button is disabled */}
              {!formData.situationDescription.trim() && (
                <div className="text-amber-600 bg-amber-50 p-3 rounded-lg text-sm">
                  💡 Please describe your situation above to enable the AI analysis button.
                </div>
              )}

              {error && (
                <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-2xl mr-2">📊</span>
              Qualification Analysis
            </h2>

            {!results && !loading && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🤖</div>
                <p className="text-gray-500 text-lg">
                  Fill out your information and click "Analyze Qualification" to get your AI-powered loan analysis
                </p>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="animate-pulse">
                  <div className="text-6xl mb-4">🧠</div>
                  <p className="text-green-600 text-lg font-medium">
                    AI is analyzing your situation across all loan programs...
                  </p>
                  <div className="mt-4 space-y-2 text-sm text-gray-500">
                    <div>• Checking federal guidelines</div>
                    <div>• Analyzing debt-to-income ratios</div>
                    <div>• Evaluating program eligibility</div>
                    <div>• Generating recommendations</div>
                  </div>
                </div>
              </div>
            )}

            {results && (
              <div className="space-y-6">
                {/* Overall Assessment */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                    <span className="mr-2">🎯</span>
                    Overall Assessment
                  </h3>
                  <p className="text-green-700">{results.overall_assessment || results.overallAssessment}</p>
                </div>

                {/* Program Analysis */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Loan Program Analysis</h3>
                  <div className="space-y-4">
                    {results.programs.map((program, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-lg flex items-center">
                            <span className="mr-2">{getQualificationIcon(program.qualified)}</span>
                            {getLoanTypeLabel(program.loan_type || program.type)} Loan
                          </h4>
                          <div className={`text-sm font-medium ${getQualificationColor(program.qualified)}`}>
                            {program.confidence}% confidence
                          </div>
                        </div>
                        
                        <p className="text-gray-700 mb-3">{program.details}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium text-gray-900 mb-2">Requirements:</h5>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {program.requirements.map((req, i) => (
                                <li key={i} className="flex items-start">
                                  <span className="text-blue-500 mr-2">•</span>
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-medium text-gray-900 mb-2">Next Steps:</h5>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {(program.next_steps || program.nextSteps || []).map((step, i) => (
                                <li key={i} className="flex items-start">
                                  <span className="text-green-500 mr-2">•</span>
                                  {step}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Insights */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <span className="mr-2">💡</span>
                    AI Insights
                  </h3>
                  <ul className="text-blue-700 space-y-2">
                    {(results.ai_insights || results.aiInsights || []).map((insight, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* AI Follow-up Questions */}
                {(results.ai_questions || []).length > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <h3 className="font-semibold text-purple-800 mb-4 flex items-center">
                      <span className="mr-2">🤔</span>
                      AI has some follow-up questions to help optimize your loan options:
                    </h3>
                    <div className="space-y-4">
                      {results.ai_questions.map((question, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-start mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-3 mt-0.5">
                              {question.question_type}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 mb-1">{question.question}</p>
                              <p className="text-sm text-gray-600">{question.context}</p>
                            </div>
                          </div>
                          {question.options && question.options.length > 0 && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {question.options.map((option, optionIndex) => (
                                <button
                                  key={optionIndex}
                                  className="text-left p-2 text-sm bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-200 rounded transition-colors"
                                  onClick={() => {
                                    // You could implement answer handling here
                                    console.log('User selected:', option);
                                  }}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-sm text-purple-700 bg-purple-100 rounded p-3">
                      💡 <strong>Pro Tip:</strong> Answering these questions can help us provide more specific guidance for your situation. Consider speaking with a loan officer about these topics.
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Important Disclaimer</h3>
                  <p className="text-yellow-700 text-sm">
                    This analysis is for educational purposes and based on general guidelines. Actual loan approval depends on complete financial review, property appraisal, and lender-specific criteria. Contact a licensed mortgage professional for official pre-qualification.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Debug Panel for API request/response */}
      {(lastRequest || lastResponse) && (
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mt-6">
          <h4 className="font-semibold text-gray-800 mb-2">Debug Info</h4>
          {lastRequest && (
            <div className="mb-2">
              <span className="font-medium">Last Request:</span>
              <pre className="bg-gray-200 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(lastRequest, null, 2)}</pre>
            </div>
          )}
          {lastResponse && (
            <div>
              <span className="font-medium">Last Response/Error:</span>
              <pre className="bg-gray-200 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(lastResponse, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoanQualificationSimulator;
